import type { HPOCitation, HPOCode } from '@/features/terminology/hpo/HPOAPIClient'
import { isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import type {
  ClinicalSummaryCitation,
  ClinicalSummaryReview,
  ClinicalSummaryTermReview,
} from './clinicalSummaryReviewTypes'
import { clinicalSummaryFingerprint } from './clinicalSummaryReviewTypes'

/**
 * Normalize an HPO identifier to a stable `HP:NNNNNNN` comparison key so that
 * `HP_0001250`, `hp:0001250`, and a bare `0001250` all collide.
 */
export function normalizePhenotypeKey(value?: string): string {
  if (!value) {
    return ''
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  const underscoreMatch = trimmed.match(/HP_\d+/i)
  if (underscoreMatch) {
    return underscoreMatch[0].replace('_', ':').toUpperCase()
  }
  const colonMatch = trimmed.match(/HP:\d+/i)
  if (colonMatch) {
    return colonMatch[0].toUpperCase()
  }
  const digitsMatch = trimmed.match(/\b(\d{7})\b/)
  if (digitsMatch) {
    return `HP:${digitsMatch[1]}`
  }
  return trimmed.toUpperCase()
}

export interface LiveExtractionTerm extends HPOCode {
  category?: CategorizedHPOOption['category']
}

export interface LiveMergeInput {
  summary: string
  clinicalDiagnosis: CategorizedHPOOption[]
  review?: ClinicalSummaryReview | null
  extraction: LiveExtractionTerm[]
  /**
   * The clinician replaced most of the summary. Service terms the new text no longer returns stay on
   * the order, as `priorOnly`, until the clinician answers the replace prompt.
   */
  replaceMode?: boolean
  /**
   * Treat an extracted term the order does not carry as a removal rather than a
   * new addition.
   *
   * Only true for the first extraction after a saved order is opened, where the
   * summary is still the text the decisions were made against. A term the
   * clinician had accepted would have been saved under some category, so an
   * extractable phrase with no term on the order means they took it off — and
   * that is the only way to recover a removal whose tombstone was never
   * persisted.
   *
   * Never true while the clinician is editing: once the text changes, a phrase
   * appearing for the first time is a genuine addition.
   */
  inferRemovalsFromExtraction?: boolean
}

/** Why an annotated extraction needs the clinician. Not persisted: it returns with the next extraction. */
export type AnnotationReason = 'prenatal' | 'low-confidence'

/**
 * The extraction attributes the finding to the mother or another relative. These are left out of
 * the patient's phenotypes; the fetus's prenatal findings are the patient's and stay.
 */
export function describesRelative(term: Pick<HPOCode, 'subject'>): boolean {
  return term.subject === 'maternal' || term.subject === 'family'
}

/** An annotated extraction narrowed to what the legacy Clinical Summary form lists: the patient's present findings. */
export function presentPatientFindings<T extends Pick<HPOCode, 'assertion' | 'subject'>>(extraction: T[]): T[] {
  return extraction.filter((term) => term.assertion !== 'absent' && !describesRelative(term))
}

export interface LiveMergeSummaryFlags {
  added: string[]
  needsInput: string[]
  notInSummary: string[]
  /** Needs-input codes whose recorded presence/absence the summary now contradicts. */
  contradictions: string[]
  /** Every code the extraction annotated, with its reason or null; codes it did not annotate are absent. */
  annotationReasons: Record<string, AnnotationReason | null>
}

export interface LiveMergeResult {
  clinicalDiagnosis: CategorizedHPOOption[]
  review: ClinicalSummaryReview
  summaryFlags: LiveMergeSummaryFlags
  /** Replace mode: service terms only the previous text supported, kept active for the prompt. */
  priorOnly: string[]
  /** The `priorOnly` codes that were still waiting on the clinician (needs-input) before this merge. */
  priorOnlyQuestions: string[]
  /** Replace mode: clinician-owned terms the new text does not return, kept either way. */
  keptByClinician: string[]
}

interface DeduplicatedExtraction {
  code: string
  label: string
  /**
   * Whether `label` is the ontology's primary name rather than the synonym the service
   * matched. PhenoML reports the matched wording in `description`, so only a response that
   * carries its own `label` field names the term authoritatively. Older deployments send
   * none, and a synonym must never overwrite a name the order already holds.
   */
  labelIsPrimary: boolean
  category: CategorizedHPOOption['category']
  citations: ClinicalSummaryCitation[]
  citationTextCandidates: Set<string>
  annotated: boolean
  assertion?: HPOCode['assertion']
  subject?: HPOCode['subject']
  lowConfidence: boolean
}

/**
 * Subject first: a negated prenatal finding still goes to the clinician, not to Pertinent negative.
 *
 * A weak match is asked about whichever way the note puts it. The name is the uncertain part —
 * "sleepiness" for Drowsiness is a guess about *which* term was meant, and negating it does not
 * make that guess safer: a pertinent negative the clinician never confirmed still reaches the lab
 * as a positive claim that the patient lacks the finding.
 */
function annotationReason(extracted: DeduplicatedExtraction): AnnotationReason | null {
  if (extracted.subject === 'prenatal') return 'prenatal'
  return extracted.lowConfidence ? 'low-confidence' : null
}

function isAbsent(category: CategorizedHPOOption['category']): boolean {
  return category === 'Excluded'
}

function validCitation(summary: string, citation: HPOCitation): ClinicalSummaryCitation | null {
  const begin = citation.begin_offset
  const end = citation.end_offset
  if (
    !Number.isInteger(begin) ||
    !Number.isInteger(end) ||
    begin < 0 ||
    end <= begin ||
    end > summary.length ||
    citation.text !== summary.slice(begin, end)
  ) {
    return null
  }
  return { begin, end }
}

function deduplicateExtraction(summary: string, extraction: LiveExtractionTerm[]): Map<string, DeduplicatedExtraction> {
  const terms = new Map<string, DeduplicatedExtraction>()

  for (const extracted of extraction) {
    const code = normalizePhenotypeKey(extracted.code)
    if (!code || describesRelative(extracted)) {
      continue
    }

    const current: DeduplicatedExtraction = terms.get(code) ?? {
      code,
      label: extracted.label ?? extracted.description,
      labelIsPrimary: Boolean(extracted.label?.trim()),
      category: extracted.category ?? 'Key',
      citations: [],
      citationTextCandidates: new Set<string>(),
      annotated: false,
      lowConfidence: false,
    }
    // Duplicates of one code: any patient or present mention wins.
    if (extracted.assertion || extracted.subject || extracted.flags) current.annotated = true
    if (extracted.assertion && current.assertion !== 'present') current.assertion = extracted.assertion
    if (extracted.subject && current.subject !== 'patient') {
      current.subject = extracted.subject === 'patient' || !current.subject ? extracted.subject : current.subject
    }
    if (extracted.flags?.includes('low-confidence')) current.lowConfidence = true
    if (!extracted.category) {
      const negated = current.assertion === 'absent' && (!current.subject || current.subject === 'patient')
      current.category = negated ? 'Excluded' : 'Key'
    }
    const citationKeys = new Set(current.citations.map(({ begin, end }) => `${begin}:${end}`))

    for (const citation of extracted.citations ?? []) {
      current.citationTextCandidates.add(citation.text)
      const valid = validCitation(summary, citation)
      const citationKey = valid ? `${valid.begin}:${valid.end}` : ''
      if (valid && !citationKeys.has(citationKey)) {
        current.citations.push(valid)
        citationKeys.add(citationKey)
      }
    }

    terms.set(code, current)
  }

  return terms
}

function normalizedReview(summary: string, review?: ClinicalSummaryReview | null): ClinicalSummaryReview {
  const terms: ClinicalSummaryReview['terms'] = {}
  for (const [rawCode, termReview] of Object.entries(review?.terms ?? {})) {
    const code = normalizePhenotypeKey(rawCode)
    if (code && !terms[code]) {
      terms[code] = {
        ...termReview,
        citations: termReview.citations.map((citation) => ({ ...citation })),
      }
    }
  }

  const tombstones = [
    ...new Set((review?.tombstones ?? []).map(normalizePhenotypeKey).filter((code): code is string => Boolean(code))),
  ]
  return {
    schemaVersion: 1,
    writerMode: 'merged-review-v1',
    summaryFingerprint: clinicalSummaryFingerprint(summary),
    terms,
    tombstones,
  }
}

/** The clinician added the term or decided its category, so a summary rewrite must not drop it. */
export function clinicianOwned(termReview: ClinicalSummaryTermReview): boolean {
  return termReview.origin === 'clinician' || termReview.categoryOwner === 'clinician'
}

/** Inflections of the same finding. Deliberately closed: "hypotonic" is a different word. */
const INFLECTIONS = ['', 's', 'es', 'ed', 'd', 'ing']

/**
 * The label reduced to the stem its inflections share, so an ontology name matches the form
 * the clinician wrote. "Vomiting" has to reach "denies vomit", which is how a pertinent
 * negative is usually phrased, while a present finding is normally written out in full.
 */
function labelStem(label: string): string {
  if (label.length > 5 && label.endsWith('ing')) return label.slice(0, -3)
  if (label.length > 3 && label.endsWith('s')) return label.slice(0, -1)
  return label
}

/**
 * Where a saved term's own words appear in the summary, as whole words and
 * case-insensitively. A term carried on the order from before the merged editor has no
 * citations, so without this the summary shows no marks until the next extraction.
 *
 * Matching is on the label's stem plus a closed set of inflections, because the ontology
 * name and the clinician's wording rarely agree: "Seizure" has to mark "seizures", and
 * "Vomiting" has to mark "vomit". Only an unambiguous single match counts: two occurrences
 * give no reason to prefer either, and guessing would mark the wrong phrase. A multi-word
 * label is never split into its parts — "Skin rash" will not claim a bare "rash", which
 * could belong to another term entirely. Anything subtler is left to the next real
 * extraction, which returns offsets rather than inferring them.
 */
function citationsFromLabel(summary: string, term: CategorizedHPOOption): ClinicalSummaryCitation[] {
  const phrase = term.label?.trim()
  if (!phrase || isHpoCode(phrase) || phrase.length < 3) return []
  const stem = labelStem(phrase.toLowerCase())
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}])${stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:${INFLECTIONS.filter(Boolean).join('|')})?(?![\\p{L}\\p{N}])`,
    'giu'
  )
  const found: ClinicalSummaryCitation[] = []
  for (const match of summary.matchAll(pattern)) {
    if (match.index === undefined) continue
    found.push({ begin: match.index, end: match.index + match[0].length })
    if (found.length > 1) break
  }
  return found.length === 1 ? found : []
}

function legacyReview(term: CategorizedHPOOption, summary: string): ClinicalSummaryTermReview {
  const addedByClinician = term.source === 'manual' || term.source === undefined
  return {
    origin: addedByClinician ? 'clinician' : 'service',
    // The clinician saved the order with this term under this category, so the category is
    // theirs even when the service first suggested the term. Without that, a weak match on
    // a later extraction reads as an unanswered question and the row is asked again.
    categoryOwner: 'clinician',
    reviewState: 'active',
    citations: citationsFromLabel(summary, term),
  }
}

function updateTermLabel(
  current: CategorizedHPOOption,
  extracted: DeduplicatedExtraction,
  category: CategorizedHPOOption['category'],
  origin: ClinicalSummaryTermReview['origin']
): CategorizedHPOOption {
  // A service-owned name refreshes from the extraction only when the extraction actually
  // knows the term's name. PhenoML reports the wording it matched in `description`
  // ("Sleepiness" for Drowsiness, "Seizures" for Seizure), and deployments without the
  // primary-label lookup send nothing else — so taking it would rename a term the order
  // already names correctly, and a rename reads as an edit that marks an untouched order
  // as changed. A placeholder is replaced either way, since anything beats a bare code.
  const storedIsPlaceholder = isHpoCode(current.label) || !current.label?.trim()
  const canRename = storedIsPlaceholder || (extracted.labelIsPrimary && !extracted.lowConfidence)
  const label = origin === 'service' && canRename ? extracted.label : current.label
  return {
    ...current,
    id: extracted.code,
    label,
    category,
    source: origin === 'clinician' ? current.source : 'phenoml',
    node: {
      ...current.node,
      id: extracted.code,
      lbl: label,
    },
  }
}

function validStoredCitations(
  summary: string,
  citations: ClinicalSummaryCitation[],
  expectedTexts: Set<string>
): ClinicalSummaryCitation[] {
  return citations.filter(
    ({ begin, end }) =>
      Number.isInteger(begin) &&
      Number.isInteger(end) &&
      begin >= 0 &&
      end > begin &&
      end <= summary.length &&
      expectedTexts.has(summary.slice(begin, end))
  )
}

/** Stored offsets that still point at real text in `summary`, whatever they now quote. */
function validCitationsInSummary(
  summary: string,
  citations: readonly ClinicalSummaryCitation[]
): ClinicalSummaryCitation[] {
  return citations
    .filter(
      ({ begin, end }) =>
        Number.isInteger(begin) && Number.isInteger(end) && begin >= 0 && end > begin && end <= summary.length
    )
    .map(({ begin, end }) => ({ begin, end }))
}

function newDiagnosis(extracted: DeduplicatedExtraction): CategorizedHPOOption {
  return {
    id: extracted.code,
    label: extracted.label,
    category: extracted.category,
    source: 'phenoml',
    node: {
      id: extracted.code,
      lbl: extracted.label,
      type: 'PhenotypicFeature',
    },
  }
}

export function mergeLiveExtraction({
  summary,
  clinicalDiagnosis,
  review,
  extraction,
  replaceMode = false,
  inferRemovalsFromExtraction = false,
}: LiveMergeInput): LiveMergeResult {
  const nextReview = normalizedReview(summary, review)
  const extractedByCode = deduplicateExtraction(summary, extraction)
  const tombstones = new Set(nextReview.tombstones)
  /**
   * The summary is still the text the clinician was looking at when they made these
   * decisions, so a term they removed stays removed. Re-asking about it on open would
   * undo the decision simply because the words are still in the note — and
   * `needs-input` blocks sending, so reopening an order to change a phone number would
   * leave it unsendable until the clinician answered a question they already answered.
   *
   * Once they edit the note, re-introducing a phrase is new intent and the term goes
   * back through review (below).
   */
  const summaryUnchangedSinceReview =
    review != null && review.summaryFingerprint === clinicalSummaryFingerprint(summary)
  const existingByCode = new Map<string, CategorizedHPOOption>()
  const diagnosisOrder: string[] = []

  for (const term of clinicalDiagnosis) {
    const code = normalizePhenotypeKey(term.id)
    if (!code || existingByCode.has(code) || tombstones.has(code)) {
      continue
    }
    existingByCode.set(code, term)
    diagnosisOrder.push(code)
    nextReview.terms[code] ??= legacyReview(term, summary)
  }

  const added: string[] = []
  const contradictions: string[] = []

  for (const [code, extracted] of extractedByCode) {
    const previousReview = nextReview.terms[code]

    if (tombstones.has(code)) {
      if (summaryUnchangedSinceReview) {
        // Reopened without an edit: the decision stands, and the phrase keeps its
        // dashed mark. Refresh the offsets from this extraction so the mark lands on
        // the words actually cited rather than a stale span.
        nextReview.terms[code] = {
          origin: previousReview?.origin ?? 'service',
          categoryOwner: previousReview?.categoryOwner ?? 'service',
          reviewState: 'removed',
          citations: extracted.citations,
          ...(previousReview?.label ? { label: previousReview.label } : {}),
        }
        continue
      }
      tombstones.delete(code)
      existingByCode.set(code, newDiagnosis(extracted))
      diagnosisOrder.push(code)
      nextReview.terms[code] = {
        origin: previousReview?.origin ?? 'service',
        categoryOwner: previousReview?.categoryOwner ?? 'service',
        reviewState: 'needs-input',
        citations: extracted.citations,
      }
      continue
    }

    const flagged = annotationReason(extracted) !== null
    const existing = existingByCode.get(code)
    if (!existing) {
      if (inferRemovalsFromExtraction) {
        /**
         * The order does not carry this term, yet the summary it was saved with
         * still contains the phrase. The clinician took it off: anything they had
         * accepted would have been saved under some category, so an extractable
         * phrase with no term is a removal.
         *
         * This is how removals survive for orders whose tombstones were never
         * persisted — every order placed before the review existed, and any whose
         * terms were removed before the first save. Treating it as a new addition
         * instead silently puts the rejected phenotype back on the order.
         */
        tombstones.add(code)
        nextReview.terms[code] = {
          origin: 'service',
          categoryOwner: 'clinician',
          reviewState: 'removed',
          citations: extracted.citations,
          ...(extracted.label && !isHpoCode(extracted.label) ? { label: extracted.label } : {}),
        }
        continue
      }
      existingByCode.set(code, newDiagnosis(extracted))
      diagnosisOrder.push(code)
      added.push(code)
      nextReview.terms[code] = {
        origin: 'service',
        categoryOwner: 'service',
        reviewState: flagged ? 'needs-input' : 'active',
        citations: extracted.citations,
      }
      continue
    }

    const termReview = previousReview ?? legacyReview(existing, summary)
    // A decision made against the same cited text is not asked again. Stored offsets go
    // stale when text before the phrase changes, so such an edit can re-raise it.
    const decidedOnSameEvidence =
      termReview.reviewState === 'active' &&
      validStoredCitations(summary, termReview.citations, extracted.citationTextCandidates).length > 0
    const categoryConflict =
      termReview.categoryOwner === 'clinician' &&
      isAbsent(existing.category) !== isAbsent(extracted.category) &&
      !decidedOnSameEvidence
    if (categoryConflict) {
      contradictions.push(code)
    }
    const category = termReview.categoryOwner === 'clinician' ? existing.category : extracted.category
    const citations =
      extracted.citations.length > 0
        ? extracted.citations
        : validStoredCitations(summary, termReview.citations, extracted.citationTextCandidates)
    existingByCode.set(code, updateTermLabel(existing, extracted, category, termReview.origin))
    // An already-active row settles the flag. The clinician has had this term in front of
    // them and left it on the order, so re-flagging it asks a question that has an answer —
    // and R14/S07 draw a reopened order's terms as settled chips, never as questions.
    // Citations cannot carry this on their own: a note naming the finding by a synonym
    // stores none, and a submitted order's review comes back with them stripped.
    const unresolvedFlag = flagged && termReview.reviewState !== 'active'
    nextReview.terms[code] = {
      ...termReview,
      // A term re-detected after removal stays unresolved until the clinician decides.
      reviewState:
        categoryConflict || unresolvedFlag || termReview.reviewState === 'needs-input' ? 'needs-input' : 'active',
      citations,
    }
  }

  const priorOnly: string[] = []
  const priorOnlyQuestions: string[] = []
  const keptByClinician: string[] = []
  for (const code of diagnosisOrder) {
    if (extractedByCode.has(code)) {
      continue
    }
    const termReview = nextReview.terms[code]
    if (replaceMode) {
      // The previous text's offsets mean nothing in the new one.
      if (clinicianOwned(termReview)) {
        keptByClinician.push(code)
        nextReview.terms[code] = { ...termReview, citations: [] }
      } else {
        // A question about text that is gone becomes part of the prompt's one decision.
        priorOnly.push(code)
        if (termReview.reviewState === 'needs-input') priorOnlyQuestions.push(code)
        nextReview.terms[code] = { ...termReview, reviewState: 'active', citations: [] }
      }
      continue
    }
    if (termReview.origin === 'service') {
      nextReview.terms[code] = {
        ...termReview,
        reviewState: 'not-in-summary',
        citations: [],
      }
    }
  }

  for (const code of tombstones) {
    if (!extractedByCode.has(code)) {
      const previousReview = nextReview.terms[code]
      const storedCitations = validCitationsInSummary(summary, previousReview?.citations ?? [])
      nextReview.terms[code] = {
        origin: previousReview?.origin ?? 'service',
        categoryOwner: previousReview?.categoryOwner ?? 'service',
        reviewState: 'removed',
        // The phrase keeps its dashed mark: R14 and S07 both draw a removed term's words
        // still underlined in the summary, so the clinician can see what they took off the
        // order and Restore has somewhere to put the marks back. Offsets that the edits
        // since have invalidated are dropped, the same test the live marks use.
        //
        // A reopened order has no stored offsets at all — the server rebuilds the review
        // from its own rows and cannot know character positions — so fall back to locating
        // the label in the text, exactly as a term with no review at all is handled.
        citations:
          storedCitations.length > 0
            ? storedCitations
            : citationsFromLabel(summary, {
                id: code,
                label: previousReview?.label ?? '',
                category: 'Excluded',
                node: { id: code, lbl: previousReview?.label ?? '', type: 'PhenotypicFeature' },
              }),
        ...(previousReview?.label ? { label: previousReview.label } : {}),
      }
    }
  }

  const diagnosisCodes = new Set(diagnosisOrder)
  for (const code of Object.keys(nextReview.terms)) {
    if (!diagnosisCodes.has(code) && !tombstones.has(code)) {
      delete nextReview.terms[code]
    }
  }
  nextReview.tombstones = [...tombstones]

  const annotationReasons: Record<string, AnnotationReason | null> = {}
  for (const [code, extracted] of extractedByCode) {
    if (extracted.annotated) annotationReasons[code] = annotationReason(extracted)
  }

  const needsInput: string[] = []
  const notInSummary: string[] = []
  for (const [code, termReview] of Object.entries(nextReview.terms)) {
    if (termReview.reviewState === 'needs-input') {
      needsInput.push(code)
    } else if (termReview.reviewState === 'not-in-summary') {
      notInSummary.push(code)
    }
  }

  return {
    clinicalDiagnosis: diagnosisOrder.map((code) => existingByCode.get(code)!),
    review: nextReview,
    summaryFlags: { added, needsInput, notInSummary, contradictions, annotationReasons },
    priorOnly,
    priorOnlyQuestions,
    keptByClinician,
  }
}

function normalizedWords(value: string): string[] {
  return value.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []
}

function longestCommonSubsequenceLength(left: string[], right: string[]): number {
  const row = new Array<number>(right.length + 1).fill(0)
  for (const leftWord of left) {
    let diagonal = 0
    for (let index = 1; index <= right.length; index += 1) {
      const previous = row[index]
      row[index] = leftWord === right[index - 1] ? diagonal + 1 : Math.max(row[index], row[index - 1])
      diagonal = previous
    }
  }
  return row[right.length]
}

export function isReplaceMost(previous: string, next: string): boolean {
  const previousWords = normalizedWords(previous)
  const nextWords = normalizedWords(next)
  const largestWordCount = Math.max(previousWords.length, nextWords.length)
  if (largestWordCount === 0) {
    return false
  }
  const unchangedWords = longestCommonSubsequenceLength(previousWords, nextWords)
  return (largestWordCount - unchangedWords) / largestWordCount > 0.5
}

export interface ReplaceSummaryPromptPayload {
  orphanCount: number
  orphanCodes: string[]
  /** Orphan codes that were open questions; Keep everything leaves them for the clinician to answer. */
  questionCodes: string[]
  keptCount: number
}

/** Counts phenotype rows tagged not-in-summary (R11 orphan note). */
export function orphanNoteCount(review?: ClinicalSummaryReview | null): number {
  return Object.values(review?.terms ?? {}).filter((term) => term.reviewState === 'not-in-summary').length
}

/**
 * Phenotypes on an order that has no review yet, all active and unquoted, stamped with `summary`.
 * Drafts started before the merged Clinical Summary carry phenotypes the clinician already
 * categorised but no review; this gives them one so later edits are checked against that text.
 *
 * Their categories are recorded as the clinician's, because that is what they are: the terms
 * sat on a saved order under those categories. Calling them the service's would make every
 * later extraction treat a weak match as an open question and send a term the clinician had
 * already accepted back for review, over and over.
 */
export function reviewForUnreviewedDiagnosis(
  clinicalDiagnosis: readonly CategorizedHPOOption[],
  summary: string
): ClinicalSummaryReview {
  return {
    schemaVersion: 1,
    writerMode: 'merged-review-v1',
    summaryFingerprint: clinicalSummaryFingerprint(summary),
    terms: Object.fromEntries(
      clinicalDiagnosis
        .map((term) => [normalizePhenotypeKey(term.id), legacyReview(term, summary)] as const)
        .filter(([code]) => Boolean(code))
    ),
    tombstones: [],
  }
}

/**
 * Whether the review still owes the clinician something: a summary edited since the last merge,
 * or a row that needs their input. An order with no review has never been through the merged
 * editor, so its phenotypes are as the clinician left them; the editor stamps a review on the
 * first edit to its summary.
 */
export function blocksSubmit(review?: ClinicalSummaryReview | null, summary?: string): boolean {
  if (review != null && summary !== undefined && review.summaryFingerprint !== clinicalSummaryFingerprint(summary)) {
    return true
  }
  return Object.values(review?.terms ?? {}).some(({ reviewState }) => reviewState === 'needs-input')
}
